/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { GlobalConstants } from '../../../../../feature/src/lib/constants/global-constants';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { MenuItems, SubMenuItems } from 'libs/sysque/toolbelt/feature/src/lib/models/menu-items';

export class RightClickMenuUpdater {
  private excludedShapes: string[] = ['CustomContent', 'BuildingLayout', 'Accessories', 'Equipment', 'Assemblies'];
  private windowName = '';
  public takeOffItems: SubMenuItems[] = [];
  public menuItems: MenuItems[] = [];
  private id = 0;
  private sysqueGaugeVariable = '';
  private releaseVersion = '';
  private excludedVersions = [ '11.4', '11.4.1' ];

  public menuItemsUpdate(
    item: Item,
    defaultCategory: string,
    windowName: string,
    selectedCategory: string,
    gaugeVariable: string,
    releaseVersion: string
  ): MenuItems[] {
    if (!item) {
      return [];
    }
    this.windowName = windowName;
    this.sysqueGaugeVariable = gaugeVariable;
    this.releaseVersion = releaseVersion;
    switch (this.windowName) {
      case GlobalConstants.Dockable:
        return this.menuItemsUpdateDockable(item, defaultCategory, selectedCategory);
      case GlobalConstants.AddFittings:
      case GlobalConstants.ManageMode:
        return this.menuItemsUpdateAddFittingsAndManageMode(item);
    }
    return this.menuItems;
  }

  public menuItemsUpdateDockable(item: Item, defaultCategory: string, selectedCategory: string): MenuItems[] {
    this.menuItems = [];
    if (!item.image) {
      this.menuItems.push(
        { text: GlobalConstants.CopyMenu },
        { text: GlobalConstants.DeleteMenu },
        { text: GlobalConstants.DownloadLatestFittingMenu }
      );
      return this.menuItems;
    }
    switch (item.itemType) {
      case GlobalConstants.ElectricalTypeItems:
        this.dockableElectricalTypeItems(item);
        break;
      case GlobalConstants.AddDefaultItem:
        this.dockableAddDefaultItems(item, defaultCategory, selectedCategory);
        break;
    }
    return this.menuItems;
  }

  public menuItemsUpdateAddFittingsAndManageMode(item: Item): MenuItems[] {
    this.menuItems = [];
    if (!item.image) {
      this.menuItems.push({ text: GlobalConstants.CopyMenu }, { text: GlobalConstants.DeleteMenu });
      return this.menuItems;
    }
    switch (item.itemType) {
      case GlobalConstants.ElectricalTypeItems:
        this.addFittingsManageModeElectricalTypeItems();
        break;
      case GlobalConstants.AddDefaultItem:
        this.addFittingsManageModeAddDefaultItems(item);
        break;
    }
    return this.menuItems;
  }

  // #region Dockable menu options

  public dockableElectricalTypeItems(item: Item): void {
    if (!item.isSysQueConnectOn) {
      this.menuItems.push({ text: GlobalConstants.CopyMenu }, { text: GlobalConstants.DuplicateMenu });

      if (!item.isEditViewSettings) {
        this.menuItems.push({ text: GlobalConstants.EditMenu }, { text: GlobalConstants.DeleteMenu });
      }

      if (item.id !== null && item.id !== '' && item.shape !== GlobalConstants.CustomContent) {
        this.menuItems.push({ text: GlobalConstants.DownloadLatestFittingMenu });
      }
    } else {
      this.addTakeOffMenuItems(GlobalConstants.TakeOffByLength);
    }
  }

  public dockableAddDefaultItems(item: Item, defaultCategory: string, selectedCategory: string): void {
    if (!item.isSysQueConnectOn && item.shape !== GlobalConstants.Assemblies) {
      this.menuItems.push({ text: GlobalConstants.CopyMenu });
    }

    this.dockableTakeOffAndTakeOffCutIn(item);

    if (!item.isSysQueConnectOn) {
      if (!this.excludedShapes.includes(item.shape)) {
        this.dockableForOtherShapes(item);
      } else {
        this.dockableForSelectedShapes(item, defaultCategory, selectedCategory);
      }
      if(this.releaseVersion && !this.excludedVersions.includes(this.releaseVersion)) {
        this.menuItems.push({ text: 'Report Issue' })
      }
    }
  }

  public dockableTakeOffAndTakeOffCutIn(item: Item): void {
    if ((item.isHanger == null || item.isHanger == '') && !item.isDisabled) {
      this.createTakeOffItems(item);
      this.addTakeOffMenuItems(GlobalConstants.TakeOff);
      if (
        item.offsetType !== null &&
        item.offsetType !== '' &&
        item.fittingType == GlobalConstants.MultiPort &&
        !item.offsetType.includes(GlobalConstants.TwoPointSaddle) &&
        !item.offsetType.includes(GlobalConstants.Kick90)
      ) {
        this.createTakeOffItems(item);
        this.addTakeOffMenuItems(GlobalConstants.TakeOffCutIn);
      }
    }
  }

  private addTakeOffMenuItems(menuItemText: string): void {
    let menuItems: MenuItems[] = [];

    if (this.takeOffItems.length > 0 && this.takeOffItems[0].text !== '') {
      const parentId = (this.id++).toString();
      const subItems = this.takeOffItems.map(item => ({ ...item, parentId }));
      menuItems = [{ text: menuItemText, id: parentId, items: subItems }];
    } else {
      menuItems = [{ text: menuItemText }];
    }

    this.menuItems.push(...menuItems);
  }

  public createTakeOffItems(item: Item): void {
    this.takeOffItems = [];
    const sizeRange = item.sizeRange.split(',');

    sizeRange.forEach((size) => {
      if (!item.isSysQueConnectOn) {
        if (!size.includes('*')) {
          this.takeOffItems.push({ text: size });
        }
      } else {
        if (this.sysqueGaugeVariable.includes(GlobalConstants.DIAMETER)) {
          this.getTakeOffSizeForConduit(size, item);
        } else {
          this.getTakeOffSizeForCableTray(size, item);
        }
      }
    });
  }

  public getTakeOffSizeForCableTray(size: string, item: Item): void {
    const [_, height, width] = this.sysqueGaugeVariable.split(',');
    const baseSize = `${width}x${height}`;

    if (size.startsWith(baseSize+'x')) {
      this.takeOffItems.push({ text: size });
    } else {
      if (item.fittingType === GlobalConstants.Cap) {
        if (size === baseSize) {
          this.takeOffItems.push({ text: size });
        }
      } else {
        if (size.startsWith(baseSize)) {
          this.takeOffItems.push({ text: size });
        }
      }
    }
  }

  public getTakeOffSizeForConduit(size: string, item: Item): void {
    const diameter = this.sysqueGaugeVariable.split(',')[1];

    if (size.includes('x')) {
      if (size.startsWith(`${diameter}x`)) {
        this.takeOffItems.push({ text: size });
      }
    } else {
      if (item.fittingType === GlobalConstants.Cap) {
        if (size === diameter) {
          this.takeOffItems.push({ text: size });
        }
      } else {
        if (size.startsWith(diameter)) {
          this.takeOffItems.push({ text: size });
        }
      }
    }
  }

  public dockableForOtherShapes(item: Item): void {
    const isFittingTypeTee =
      item.fittingType === GlobalConstants.LadderCableTrayTee ||
      item.fittingType === GlobalConstants.ChannelCableTrayTee;
    const isBendTypeValid = item.bendType === null || item.bendType === '';
    const isOffsetTypeSaddle =
      item.offsetType !== null && item.offsetType.toLowerCase().includes(GlobalConstants.Saddle);
    const isOffsetTypeStub =
      item.offsetType !== null &&
      item.shape !== GlobalConstants.ConduitCategory &&
      item.offsetType.toLowerCase().includes(GlobalConstants.Stub);

    switch (item.isDisabled) {
      case true:
        this.menuItems.push({ text: GlobalConstants.EnableMenu });
        if (!item.isEditViewSettings) {
          this.menuItems.push({ text: GlobalConstants.DeleteMenu });
        }
        break;
      case false:
        if (!item.isEditViewSettings) {
          this.menuItems.push({ text: GlobalConstants.EditMenu }, { text: GlobalConstants.DeleteMenu });
        }

        if (!(isFittingTypeTee && !isBendTypeValid) && !isOffsetTypeSaddle && !isOffsetTypeStub) {
          switch (item.isDefault) {
            case true:
              this.menuItems.push({ text: GlobalConstants.RemoveDefaultMenu });
              break;
            case false:
              this.menuItems.push({ text: GlobalConstants.SetAsDefaultMenu });
              break;
          }
        }
        this.menuItems.push({ text: GlobalConstants.DisableMenu });
        break;
    }
    this.menuItems.push({ text: GlobalConstants.DownloadLatestFittingMenu });
  }

  public dockableForSelectedShapes(item: Item, defaultCategory: string, selectedCategory: string): void {
    if (item.isHanger && !item.isDisabled && selectedCategory == GlobalConstants.BuildingLayoutCategory) {
      if (
        defaultCategory === GlobalConstants.ConduitCategory ||
        defaultCategory === GlobalConstants.FieldFabConduitCategory
      ) {
        this.menuItems.push({ text: GlobalConstants.TakeOffAsMultiRackHanger });
      }

      this.menuItems.push({ text: GlobalConstants.PlaceHanger });
    }

    if (!item.isDisabled && item.shape !== GlobalConstants.Assemblies && !item.isEditViewSettings) {
      this.menuItems.push({ text: GlobalConstants.EditMenu });
    }

    if (!item.isEditViewSettings) {
      this.menuItems.push({ text: GlobalConstants.DeleteMenu });
    }

    if (item.shape !== GlobalConstants.Assemblies && item.shape !== GlobalConstants.CustomContent && item.id !== null) {
      this.menuItems.push({ text: GlobalConstants.DownloadLatestFittingMenu });
    }
  }

  // #endregion

  // #region Add Fittings and Manage Mode menu options

  public addFittingsManageModeElectricalTypeItems(): void {
    this.menuItems.push(
      { text: GlobalConstants.CopyMenu },
      { text: GlobalConstants.EditMenu },
      { text: GlobalConstants.DeleteMenu }
    );
    if (this.windowName === GlobalConstants.AddFittings) {
      this.menuItems.push({ text: GlobalConstants.ShowInEnclosingFolderLabel });
    }
  }

  public addFittingsManageModeAddDefaultItems(item: Item): void {
    if (item.shape !== GlobalConstants.Assemblies) {
      this.menuItems.push({ text: GlobalConstants.CopyMenu });
    }

    if (!this.excludedShapes.includes(item.shape)) {
      this.addFittingsManageModeForOtherShapes(item);
    } else {
      this.addFittingsManageModeForSelectedShapes(item);
    }

    if (this.windowName === GlobalConstants.AddFittings) {
      this.menuItems.push({ text: GlobalConstants.ShowInEnclosingFolderLabel });
    }
  }

  public addFittingsManageModeForOtherShapes(item: Item): void {
    const isFittingTypeTee =
      item.fittingType === GlobalConstants.LadderCableTrayTee ||
      item.fittingType === GlobalConstants.ChannelCableTrayTee;
    const isBendTypeValid = item.bendType === null || item.bendType === '';
    const isOffsetTypeSaddle =
      item.offsetType !== null && item.offsetType.toLowerCase().includes(GlobalConstants.Saddle);
    const isOffsetTypeStub =
      item.offsetType !== null &&
      item.shape !== GlobalConstants.ConduitCategory &&
      item.offsetType.toLowerCase().includes(GlobalConstants.Stub);

    switch (item.isDisabled) {
      case true:
        this.menuItems.push({ text: GlobalConstants.EnableMenu }, { text: GlobalConstants.DeleteMenu });
        break;
      case false:
        this.menuItems.push(
          { text: GlobalConstants.EditMenu },
          { text: GlobalConstants.DeleteMenu },
          { text: GlobalConstants.DisableMenu }
        );
        if (!(isFittingTypeTee && !isBendTypeValid) && !isOffsetTypeSaddle && !isOffsetTypeStub) {
          switch (item.isDefault) {
            case true:
              this.menuItems.push({ text: GlobalConstants.RemoveDefaultMenu });
              break;
            case false:
              this.menuItems.push({ text: GlobalConstants.SetAsDefaultMenu });
              break;
          }
        }
        break;
    }
  }

  public addFittingsManageModeForSelectedShapes(item: Item): void {
    if (!item.isDisabled) {
      this.menuItems.push({ text: GlobalConstants.EditMenu }, { text: GlobalConstants.DeleteMenu });
    }
    if (item.isDisabled && item.shape === GlobalConstants.BuildingLayoutCategory) {
      this.menuItems.push({ text: GlobalConstants.DeleteMenu });
    }
  }

  // #endregion
}